/**
 * TV Schedule module - handles schedule display and GraphQL data fetching
 */

import {
  isNowBetweenDates,
  getDifferenceInSeconds,
  updateURLParameter,
  removeURLParameter,
} from './utils.js';

let schedule = false;
let headerInner = null;

export function getSchedule() {
  return schedule;
}

export function initSchedule(headerInnerElement) {
  headerInner = headerInnerElement;
}

function getGuideOffset() {
  const cover = document.querySelector('.row .cover');
  if (cover) return cover.offsetWidth + 10;
  return 96;
}

export function updateTimelines(startTime) {
  if (!headerInner) return;

  const now = new Date();
  const ts = now.getTime();
  const dif = (startTime - ts) / 1000;
  const offset = getGuideOffset();

  headerInner.style.transform = `translateX(${Math.round(offset + dif * 0.3)}px)`;

  setTimeout(function () {
    updateTimelines(startTime);
  }, 1000);
}

export function checkForActive(player, loadPageCallback) {
  if (!schedule || schedule.length === 0) return;

  const tvGuide = document.querySelector('.tv-guide');
  if (!tvGuide) return;

  const rows = tvGuide.querySelectorAll('.row');
  const now = new Date();
  const ts = now.getTime();

  for (let a = 0; a < rows.length; a++) {
    const row = rows[a];
    const epsWrap = row.querySelector('.episodes');
    const eps = row.querySelectorAll('.episode');
    const slug = row.dataset.channel;

    if (!epsWrap) continue;

    let match = false;

    for (let b = 0; b < schedule.length; b++) {
      const channel = schedule[b];

      if (slug === channel.slug) {
        match = channel;

        if (match.schedule && match.schedule[0]) {
          const programing = match.schedule[0];
          const startDate = new Date(programing.startTime).getTime();
          const dif = (startDate - ts) / 1000;

          const offset = getGuideOffset();
          epsWrap.style.transform = `translateX(${offset + dif * 0.3}px)`;
        }
      }
    }

    if (match && match.schedule) {
      for (let b = 0; b < eps.length; b++) {
        const ep = eps[b];
        const programing = match.schedule[b];

        if (!programing) continue;

        ep.href = updateURLParameter(ep.href, 'startTime', programing.startTime);

        if (isNowBetweenDates(programing.startTime, programing.endTime)) {
          ep.href = updateURLParameter(ep.href, 'active', true);
          ep.classList.add('active');

          const seconds = getDifferenceInSeconds(programing.startTime) * -1;
          const pad = seconds * 0.3 + 20;
          const width = ep.offsetWidth;

          if (width - pad > 100) {
            ep.style.paddingLeft = pad + 'px';
          } else {
            ep.style.paddingLeft = 'unset';
          }

          if (player.channel && match.slug === player.channel.slug) {
            const currentSlug = player.currentEp && player.currentEp.episode && player.currentEp.episode[0] ? player.currentEp.episode[0].slug : null;
            const programSlug = programing.episode && programing.episode[0] ? programing.episode[0].slug : null;
            if (player.playing && currentSlug !== programSlug && !player.loading) {
              player.loading = true;

              if (player.audio) {
                player.audio.pause();
                player.audio = false;
              }

              if (player.video) {
                player.video.pauseVideo();
                player.video = false;
              }

              loadPageCallback('next-episode', ep.href, false);
            }
          }
        } else {
          ep.href = removeURLParameter(ep.href, 'active');
          ep.classList.remove('active');
        }
      }
    }
  }
}

export async function fetchScheduleRows(player, loadPageCallback, bindPlayersCallback) {
  const tvGuide = document.querySelector('.tv-guide');
  if (!tvGuide) return;

  const skeletons = tvGuide.querySelectorAll('.row.skeleton');
  if (skeletons.length === 0) {
    // No skeletons — rows already loaded, just fetch live data
    await fetchGraphQLData(player, loadPageCallback, bindPlayersCallback);
    return;
  }

  // Start GraphQL fetch in parallel with row loading
  const graphqlPromise = fetchGraphQLData(player, loadPageCallback, bindPlayersCallback);

  for (const skeleton of skeletons) {
    const slug = skeleton.dataset.channel;
    try {
      const response = await fetch(`/schedule-row?slug=${encodeURIComponent(slug)}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const html = await response.text();
      const temp = document.createElement('div');
      temp.innerHTML = html.trim();
      const row = temp.querySelector('.row');
      if (row) {
        skeleton.replaceWith(row);
        // Apply live data to this row immediately if schedule is ready
        if (schedule) {
          checkForActive(player, loadPageCallback);
        }
      } else {
        skeleton.remove();
      }
    } catch (err) {
      console.warn(`Error fetching row ${slug}:`, err);
      skeleton.remove();
    }
  }

  // Ensure GraphQL is done, then do a final pass
  await graphqlPromise;
  checkForActive(player, loadPageCallback);
  if (bindPlayersCallback) {
    bindPlayersCallback(schedule, loadPageCallback);
  }
}

export const fetchGraphQLData = async (player, loadPageCallback, bindPlayersCallback) => {
  // Get channel slugs from the DOM (only fetch what's actually displayed)
  const rows = document.querySelectorAll('.tv-guide .row[data-channel]');
  const slugs = Array.from(rows).map(row => row.dataset.channel).filter(Boolean);

  // Channels known to break GraphQL — skip entirely.
  const brokenChannels = ['pre-prime-time'];
  const usableSlugs = slugs.filter(s => !brokenChannels.includes(s));

  let allEntries = [];

  // Batch-fetch every channel in a single query. The query resolves
  // scheduleJson on both entry types (customProgram and showFeed) via
  // inline fragments, so any channel type works without a hardcoded list.
  if (usableSlugs.length > 0) {
    const slugList = usableSlugs.map(s => `"${s}"`).join(', ');
    const query = `{
      entries(section: "channel", slug: [${slugList}]) {
        id
        title
        slug
        ... on channel_customProgram_Entry {
          scheduleJson
        }
        ... on channel_showFeed_Entry {
          scheduleJson
        }
      }
    }`;

    try {
      const response = await fetch('/api', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.data?.entries) {
          allEntries = data.data.entries;
        }
      }
    } catch (err) {
      console.warn('Error fetching channel schedules:', err);
    }
  }

  // Parse scheduleJson into schedule array, wrapping episode in array for player.js compatibility
  allEntries = allEntries.map(entry => ({
    ...entry,
    schedule: (entry.scheduleJson ? JSON.parse(entry.scheduleJson) : []).map(block => ({
      ...block,
      episode: [block.episode],
    })),
  }));

  if (allEntries.length > 0) {
    schedule = allEntries;
    checkForActive(player, loadPageCallback);
    if (bindPlayersCallback) {
      bindPlayersCallback(schedule, loadPageCallback);
    }
  }
};
